
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { sanitizeInput, validateEmail, validatePassword } from '@/lib/security';
import { validateIsraeliPhone } from '@/lib/phoneValidation';
// CSRF functionality removed - was providing minimal security benefit without server-side validation
import { useErrorMonitoring } from '@/hooks/useErrorMonitoring';

// NOTE: Client-side rate limiting has been removed as it provides no real security.
// Rate limiting is now handled server-side by Supabase Auth's built-in protection,
// which includes IP-based throttling and exponential backoff for failed attempts.
// This prevents attackers from bypassing limits by clearing localStorage or using automation.

interface Profile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  tax_id?: string;
  business_name?: string;
  business_address?: string;
  created_at: string;
  updated_at: string;
}

interface UserRole {
  role: 'user' | 'admin';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRole: string | null;
  isAdmin: boolean;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  resetPasswordWithCode: (email: string, code: string, newPassword: string) => Promise<{ error: any }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const initialLoadCompleteRef = useRef(false);
  const errorMonitoring = useErrorMonitoring();

  const isAdmin = userRole === 'admin';

  const fetchProfile = async (userId: string, isInitialLoad: boolean = false) => {
    try {
      // Add a small delay to ensure auth token is properly set
      await new Promise(resolve => setTimeout(resolve, 100));

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        if (isInitialLoad) setLoading(false);
        return;
      }

      setProfile(data);

      // Fetch user role from the new role system FIRST
      const { data: roleData, error: roleError } = await supabase
        .rpc('get_current_user_role');

      let finalRole = 'user';
      if (!roleError && roleData) {
        finalRole = roleData;
        setUserRole(roleData);
      } else {
        // Fallback: check user_roles table directly
        const { data: fallbackRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single();

        finalRole = fallbackRole?.role || 'user';
        setUserRole(finalRole);
      }

      // Identify user in Sentry for error tracking
      if (window.identifyUser) {
        window.identifyUser({ id: userId }, data);
      }

      // Only set loading to false after role is determined on initial load
      if (isInitialLoad) {
        setLoading(false);
        initialLoadCompleteRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setUserRole('user'); // Default to user role on error
      if (isInitialLoad) {
        setLoading(false);
        initialLoadCompleteRef.current = true;
      }
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Only show loading screen on initial load, not on subsequent auth state changes
          const isInitialLoad = !initialLoadCompleteRef.current;
          if (isInitialLoad) {
            setLoading(true);
          }
          // Defer profile fetch to avoid blocking auth state change
          setTimeout(() => {
            fetchProfile(session.user.id, isInitialLoad);
          }, 50);
        } else {
          setProfile(null);
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Keep loading true while fetching profile and role
        setLoading(true);
        setTimeout(() => {
          fetchProfile(session.user.id, true);
        }, 50);
      } else {
        setLoading(false);
        initialLoadCompleteRef.current = true;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      // Validate input
      if (!validateEmail(email)) {
        toast.error("שגיאה בהרשמה", {
          description: "כתובת אימייל לא תקינה",
        });

        // Report validation error
        errorMonitoring.reportAuthError('Invalid email during signup', 'signup', { email });
        return { error: { message: 'כתובת אימייל לא תקינה' } };
      }

      // Check if email already exists
      try {
        const { data: existsData } = await supabase.functions.invoke('check-email-exists', {
          body: { email: email.toLowerCase().trim() }
        });

        if (existsData?.exists) {
          toast.error("משתמש קיים", {
            description: "משתמש עם כתובת מייל זו כבר קיים. אנא התחבר או השתמש באימייל אחר",
          });
          return { error: { message: "משתמש עם כתובת מייל זו כבר קיים" } };
        }
      } catch (checkError) {
        console.error('Email existence check failed:', checkError);
      }

      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        toast.error("שגיאה בהרשמה", {
          description: passwordValidation.message,
        });
        return { error: { message: passwordValidation.message } };
      }

      // Sanitize input
      const sanitizedFirstName = sanitizeInput(firstName);
      const sanitizedLastName = sanitizeInput(lastName);

      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: sanitizedFirstName,
            last_name: sanitizedLastName,
          }
        }
      });

      if (error) {
        toast.error("שגיאה בהרשמה", {
          description: error.message,
        });
        return { error };
      }

      toast.success("הרשמה בוצעה בהצלחה", {
        description: "אנא בדוק את המייל שלך לאישור החשבון",
      });

      // Track sign up event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_up', {
          method: 'email'
        });
      }

      return { error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Basic validation
      if (!validateEmail(email)) {
        toast.error("שגיאה בהתחברות", {
          description: "כתובת אימייל לא תקינה",
        });
        return { error: { message: 'כתובת אימייל לא תקינה' } };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        // Log authentication failure for security monitoring
        try {
          await supabase.rpc('log_auth_failure', {
            email_param: email,
            failure_reason: error.message
          });
        } catch (logError) {
          console.error('Failed to log auth failure:', logError);
        }

        // Translate common auth error messages to Hebrew
        let desc = error.message;
        const errorMsg = (error.message || '').toLowerCase();

        if (errorMsg.includes('email not confirmed')) {
          desc = 'האימייל לא אומת';
        } else if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid credentials')) {
          desc = 'פרטי התחברות שגויים';
        } else if (errorMsg.includes('wrong password') || errorMsg.includes('incorrect password')) {
          desc = 'סיסמה שגויה';
        } else if (errorMsg.includes('user not found') || errorMsg.includes('email not found')) {
          desc = 'משתמש לא נמצא';
        } else if (errorMsg.includes('too many requests')) {
          desc = 'יותר מדי ניסיונות התחברות. נסה שוב מאוחר יותר';
        }
        toast.error("שגיאה בהתחברות", {
          description: desc,
        });
        return { error };
      }

      // No role restrictions - both admins and regular users can login through the same modal

      toast.success("התחברת בהצלחה", {
        description: "ברוך הבא ל-Excess Market",
      });

      // Track login event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'login', {
          method: 'email'
        });
      }

      return { error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error("שגיאה בהתנתקות", {
          description: error.message,
        });
        return;
      }

      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRole(null);

      // Clear cart and checkout state on logout
      try {
        localStorage.removeItem('materialflow-cart');
        localStorage.removeItem('selectedCartItems');

        // Clear checkout state if cart context is available globally
        if (window.cartContextRef?.clearCheckoutState) {
          window.cartContextRef.clearCheckoutState();
        }
      } catch (storageError) {
        console.error('Error clearing cart on logout:', storageError);
      }

      // Redirect to home page if user was in checkout
      const currentPath = window.location.pathname;
      if (currentPath === '/checkout') {
        window.location.href = '/';
        return;
      }

      toast.success("התנתקת בהצלחה", {
        description: "להתראות!",
      });
    } catch (error) {
      console.error('Signout error:', error);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!user) {
        return { error: new Error('User not authenticated') };
      }

      // Prevent role escalation - users cannot change their own role
      const sanitizedUpdates = { ...updates };

      // Phone validation
      if (sanitizedUpdates.phone !== undefined) {
        if (!validateIsraeliPhone(String(sanitizedUpdates.phone))) {
          toast.error('שגיאה בעדכון פרופיל', {
            description: "מספר טלפון לא תקין",
          });
          return { error: new Error('מספר טלפון לא תקין') };
        }
      }

      // Validate name fields
      if (sanitizedUpdates.first_name && sanitizedUpdates.first_name.length > 50) {
        toast.error('שגיאה בעדכון פרופיל', {
          description: "שם פרטי ארוך מדי (מקסימום 50 תווים)",
        });
        return { error: new Error('שם פרטי ארוך מדי') };
      }
      if (sanitizedUpdates.last_name && sanitizedUpdates.last_name.length > 50) {
        toast.error('שגיאה בעדכון פרופיל', {
          description: "שם משפחה ארוך מדי (מקסימום 50 תווים)",
        });
        return { error: new Error('שם משפחה ארוך מדי') };
      }

      const { error } = await supabase
        .from('profiles')
        .update(sanitizedUpdates)
        .eq('id', user.id);

      if (error) {
        toast.error("שגיאה בעדכון פרופיל", {
          description: error.message,
        });
        return { error };
      }

      // Refresh profile data
      await fetchProfile(user.id);

      toast.success("פרופיל עודכן בהצלחה");

      return { error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { error };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) throw new Error('No user logged in');

    // Validate new password strength
    if (newPassword.length < 6) {
      throw new Error('הסיסמה החדשה חייבת להכיל לפחות 6 תווים');
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      throw new Error('הסיסמה חייבת להכיל לפחות אות גדולה, אות קטנה ומספר');
    }

    // Verify current password by attempting to sign in
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email!,
      password: currentPassword,
    });

    if (verifyError) {
      throw new Error('הסיסמה הנוכחית שגויה');
    }

    // Update password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      // Convert common Supabase error messages to Hebrew
      const errorMessage = error.message.toLowerCase();
      if (errorMessage.includes('same') || errorMessage.includes('password')) {
        throw new Error('הסיסמה החדשה חייבת להיות שונה מהסיסמה הנוכחית');
      }
      throw new Error('שגיאה בעדכון הסיסמה');
    }

    // Sign out all other sessions for security
    await supabase.auth.signOut({ scope: 'others' });
  };

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: { email, type: 'send', action: 'password-reset' }
      });

      if (error) {
        toast.error("שגיאה בשליחת קוד", {
          description: error.message,
        });
        return { error };
      }

      toast.success("קוד אימות נשלח", {
        description: "אם המייל קיים במערכת, נשלח אליו קוד אימות. בדוק את תיבת המייל שלך",
      });

      return { error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error };
    }
  };

  const resetPasswordWithCode = async (email: string, code: string, newPassword: string) => {
    try {
      const response = await supabase.functions.invoke('reset-password-with-code', {
        body: { email, code, newPassword }
      });

      // Handle edge function errors (non-2xx responses)
      if (response.error) {
        const errorMessage = response.error.message || 'אירעה שגיאה באיפוס הסיסמה';
        toast.error("שגיאה באיפוס סיסמה", {
          description: errorMessage,
        });
        return { error: response.error };
      }

      // Handle application-level errors from the edge function
      if (response.data?.error) {
        toast.error("שגיאה באיפוס סיסמה", {
          description: response.data.error,
        });
        return { error: response.data };
      }

      toast.success("הסיסמה שונתה בהצלחה", {
        description: "כעת ניתן להתחבר עם הסיסמה החדשה",
      });

      return { error: null };
    } catch (error) {
      console.error('Reset password with code error:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    profile,
    userRole,
    isAdmin,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    changePassword,
    resetPassword,
    resetPasswordWithCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
